// drawer/chrome.tsx
// Design-specific chrome bits for the substance drawer, ported verbatim from
// sl-panel.jsx with inline styles + CSS-variable tokens. Reuses
// SidebarPanel/InfoTip for the infoTip passthrough in SubHead.
//
// Exported: Slash, SectionBadge, SubHead, StockToggle, CalendarGlyph,
//           DateField, AutoSaveBadge, DropArea, DropCTA

import * as React from "react";
import { Icon } from "@/components/Icon/Icon";
import { InfoTip } from "../../SidebarPanel/InfoTip";
import type { IconName } from "@/utils/types/ui";

// ── Slash ─────────────────────────────────────────────────────────────
// Small mono uppercase label used in the top chrome (sl-panel.jsx 78–79).
export function Slash({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="mono"
      style={{
        fontSize: 10,
        color: "var(--ink-3)",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        fontWeight: 700,
      }}
    >
      {children}
    </span>
  );
}

// ── SectionBadge ──────────────────────────────────────────────────────
// Small count/hint badge used inside Collapsible headers (sl-panel.jsx 171–172).
export function SectionBadge({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="mono"
      style={{
        fontSize: 9.5,
        color: "var(--ink-4)",
        padding: "1px 6px",
        borderRadius: 3,
        background: "var(--bg-sunk)",
        border: "1px solid var(--line)",
        fontWeight: 600,
      }}
    >
      {children}
    </span>
  );
}

// ── SubHead ───────────────────────────────────────────────────────────
// Sub-section header used inside Identity and other sections (sl-panel.jsx
// 175–184). Extended with an optional `infoTip` prop: when provided, renders
// the production SidebarPanel/InfoTip (ⓘ icon + hover tooltip) immediately
// after the title, so the "Public identifiers" sub-header can carry its
// current production tooltip (Task A10).
export function SubHead({
  icon,
  title,
  hint,
  count,
  action,
  infoTip,
}: {
  icon?: IconName;
  title: string;
  hint?: string;
  count?: number;
  action?: React.ReactNode;
  infoTip?: React.ReactNode;
}) {
  return (
    <div className="hstack" style={{ gap: 8, justifyContent: "space-between" }}>
      <div className="hstack" style={{ gap: 7 }}>
        {icon && (
          <span style={{ color: "var(--ink-3)", display: "inline-flex" }}>
            <Icon name={icon} size={13} />
          </span>
        )}
        <span
          className="mono"
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "var(--ink-2)",
          }}
        >
          {title}
        </span>
        {infoTip && <InfoTip content={infoTip} />}
        {count != null && <SectionBadge>{count}</SectionBadge>}
        {hint && (
          <span className="mono" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
            {hint}
          </span>
        )}
      </div>
      {action}
    </div>
  );
}

// ── StockToggle ───────────────────────────────────────────────────────
// Toggle button for in-stock / out-of-stock status (sl-panel.jsx 217–221).
export function StockToggle({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      title="Click to toggle stock status"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        padding: "4px 11px",
        borderRadius: 999,
        background: value ? "var(--accent-soft)" : "rgba(161, 75, 58, 0.10)",
        color: value ? "var(--accent-ink)" : "#a14b3a",
        border: `1px solid ${value ? "var(--accent-line)" : "#c98c7a"}`,
        cursor: "pointer",
        fontWeight: 700,
        fontSize: 10,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        fontFamily: "var(--font-mono)",
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: value ? "var(--brand-green)" : "#a14b3a",
        }}
      />
      {value ? "In stock" : "Out of stock"}
    </button>
  );
}

// ── CalendarGlyph ─────────────────────────────────────────────────────
// Inline SVG calendar icon overlaid on DateField (sl-panel.jsx 129–132).
export function CalendarGlyph({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4.5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 2.5v4M16 2.5v4" />
    </svg>
  );
}

// ── DateField ─────────────────────────────────────────────────────────
// Typeable YYYY-MM-DD text input with an overlaid native date picker that
// opens the OS calendar on click (sl-panel.jsx 135–151).
//
// · The visible input is type="text" so the user can type freely.
// · A transparent type="date" input overlays the calendar-glyph region;
//   clicking it opens the OS date picker, which writes back via onChange.
// · isoOk validates the ISO format before passing it to the native input.

const isoOk = (v: string | null | undefined): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(v ?? "");

const slpMonoInput: React.CSSProperties = {
  width: "100%",
  padding: "7px 10px",
  fontSize: 13,
  border: "1px solid var(--line-strong)",
  borderRadius: 5,
  background: "var(--panel)",
  outline: "none",
  fontFamily: "var(--font-mono)",
  color: "var(--ink)",
};

export function DateField({
  value,
  onChange,
  placeholder = "YYYY-MM-DD",
  tone,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  tone?: React.CSSProperties;
}) {
  const dateRef = React.useRef<HTMLInputElement>(null);
  return (
    <div style={{ position: "relative" }}>
      <input
        type="text"
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...slpMonoInput, paddingRight: 34, ...(tone ?? {}) }}
      />
      {/* native date input overlays the calendar button so a click opens the OS picker */}
      <input
        ref={dateRef}
        type="date"
        value={isoOk(value) ? value : ""}
        tabIndex={-1}
        onChange={(e) => onChange(e.target.value)}
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          height: "100%",
          width: 34,
          opacity: 0,
          cursor: "pointer",
          border: "none",
          padding: 0,
        }}
      />
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          right: 9,
          top: "50%",
          transform: "translateY(-50%)",
          color: "var(--ink-4)",
          pointerEvents: "none",
          display: "inline-flex",
        }}
      >
        <CalendarGlyph />
      </span>
    </div>
  );
}

// ── AutoSaveBadge ─────────────────────────────────────────────────────
// Static "Saved · now" badge displayed in edit mode (sl-panel.jsx 290–294).
export function AutoSaveBadge() {
  return (
    <span
      className="mono"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 10,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        fontWeight: 600,
        color: "var(--ink-4)",
        padding: "3px 9px",
        borderRadius: 999,
        background: "var(--bg-sunk)",
        border: "1px solid var(--line)",
      }}
    >
      <span
        style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--brand-lime)" }}
      />
      Saved · now
    </span>
  );
}

// ── DropArea ──────────────────────────────────────────────────────────
// Drag-over file drop wrapper (sl-panel.jsx 260–281). Children render
// normally; a translucent drop overlay appears on drag-enter.
export function DropArea({
  onFiles,
  label,
  sublabel,
  children,
}: {
  onFiles: (files: File[]) => void;
  label: string;
  sublabel?: string;
  children?: React.ReactNode;
}) {
  const [over, setOver] = React.useState(false);
  const depth = React.useRef(0);

  const hasFiles = (e: React.DragEvent) =>
    !!e.dataTransfer && Array.from(e.dataTransfer.types || []).includes("Files");

  const handlers = {
    onDragEnter: (e: React.DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      depth.current += 1;
      setOver(true);
    },
    onDragOver: (e: React.DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "copy";
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setOver(false);
    },
    onDrop: (e: React.DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      depth.current = 0;
      setOver(false);
      const f = Array.from(e.dataTransfer.files || []);
      if (f.length) onFiles(f);
    },
  };

  return (
    <div {...handlers} style={{ position: "relative" }}>
      {children}
      {over && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(58, 124, 95, 0.10)",
            border: "2px dashed var(--accent)",
            borderRadius: 10,
            display: "flex",
            flexDirection: "column",
            gap: 6,
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 5,
            color: "var(--accent-ink)",
          }}
        >
          <Icon name="upload" size={22} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
          {sublabel && (
            <span className="mono" style={{ fontSize: 10, opacity: 0.75 }}>
              {sublabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── DropCTA ───────────────────────────────────────────────────────────
// Dashed drop-zone CTA button (sl-panel.jsx 282–287). Typically placed
// inside a DropArea as the visible affordance.
export function DropCTA({
  label,
  sublabel,
  onClick,
}: {
  label: string;
  sublabel?: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
        padding: "18px 16px",
        borderRadius: 8,
        border: "1.5px dashed var(--line-strong)",
        background: "var(--bg-sunk)",
        color: "var(--ink-3)",
        textAlign: "center",
        cursor: "pointer",
      }}
    >
      <div style={{ marginBottom: 6, color: "var(--ink-3)" }}>
        <Icon name="upload" size={18} />
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--ink-2)" }}>{label}</div>
      {sublabel && (
        <div className="mono" style={{ fontSize: 10, color: "var(--ink-4)", marginTop: 4 }}>
          {sublabel}
        </div>
      )}
    </button>
  );
}
